import { useMixpanel } from '@common/composables/useMixpanel.js';
import { useAppSettings } from '@mms/composables/useAppSettings.js';
import mainStore from '@mms/store/mainStore.js';
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import { isEmpty } from 'lodash-es';
import { computed, reactive, readonly, toRefs, unref } from 'vue';
import { useRouter } from 'vue-router';

const state = reactive({
  authorities: {},
  isAuthenticated:
    Boolean(localStorage.getItem('token')) || Boolean(sessionStorage.getItem('token')),
  isLoading: false,
  user: {},
});

// Direct Import (userStoreState): when you need simple, read-only access to the state.
export const userStoreState = computed(() => state);

export function clearAuth() {
  axios.defaults.headers.common['Authorization'] = '';
  if (state.isImpersonating) {
    sessionStorage.clear();
  } else {
    localStorage.removeItem('token');
    localStorage.removeItem('userInfo');
  }
  state.isAuthenticated = false;
  state.user = {};
  Sentry.setUser(null);
}

export function useUserStore() {
  const router = useRouter();
  const appSettings = useAppSettings();
  const { init, reset } = useMixpanel();

  const logout = async () => {
    try {
      state.isLoading = true;
      await axios.get(`${mainStore.appBaseUrl}/auth/logout`);
    } finally {
      appSettings.removeAuthenticationStyle();
      clearAuth();
      reset();
      state.isLoading = false;
      router.go({ name: 'login' });
    }
  };

  const setAuthenticationState = (token, isSessionBased) => {
    if (token) {
      if (isSessionBased) {
        sessionStorage.setItem('token', token);
      } else {
        localStorage.setItem('token', token);
      }
    }

    const currentToken = sessionStorage.getItem('token') || localStorage.getItem('token');

    axios.defaults.headers.common['Authorization'] = `Bearer ${currentToken}`;
    state.isAuthenticated = true;
  };

  const setAuthorities = async () => {
    const {
      data: { authorities },
    } = await axios.get(`${mainStore.appBaseUrl}/authorities`);

    state.authorities = { ...authorities };
  };

  const setUserInfo = async (data = {}) => {
    try {
      const { isSessionBased, token } = data;
      state.isLoading = true;

      setAuthenticationState(token, isSessionBased);
      const {
        data: { user },
      } = await axios.get(`${mainStore.appBaseUrl}/auth/whoami`);

      state.user = { ...user };
      // check if user has verfied mobile number or not and then set the authorites
      if (isMobileVerified.value) {
        await setAuthorities();
        appSettings.setAuthenticationStyle();
      }
      Sentry.setUser({ email: user.email, id: user.id });
      initMixpanel(user);
      return user;
    } catch {
      await logout();
    } finally {
      state.isLoading = false;
    }
  };

  const initMixpanel = (user) => {
    const {
      birth_date,
      email,
      gender,
      id,
      is_active,
      is_contact_user,
      is_impersonated,
      is_owner,
      merchant,
      name,
    } = user;

    init(
      {
        birthDate: birth_date,
        distinct_id: id,
        email,
        gender: gender === 1 ? 'Male' : 'Female',
        id,
        isActive: is_active,
        isContactUser: is_contact_user,
        isImpersonated: is_impersonated,
        isOwner: is_owner,
        merchantReference: merchant?.reference ?? '',
        name,
      },
      {
        openModalsEventName: 'MMS Open Modal',
        trackOpenModals: true,
      }
    );
  };

  const setUserState = (data) => {
    state.user = { ...state.user, ...data };
  };

  const hasAuthority = (authority, atLeastOne = true) => {
    if (isEmpty(state.user)) {
      return false;
    }
    if (state.user?.is_admin || !authority) {
      return true;
    }

    const resolvedAuthority = unref(authority);

    if (Array.isArray(resolvedAuthority)) {
      if (atLeastOne) {
        return resolvedAuthority.some((x) => state.user?.authorities.includes(x));
      }
      return resolvedAuthority.every((x) => state.user?.authorities.includes(x));
    }

    return state.user?.authorities?.includes(resolvedAuthority);
  };

  const isSameUser = (email) => {
    return state.user.email === email;
  };

  const isCurrentUserAdmin = computed(() => {
    return state.user.is_admin;
  });

  const isMobileVerified = computed(() => {
    const eKYC = state.user?.eKYC;
    return Boolean(eKYC?.is_mobile_number_verified);
  });

  return {
    ...toRefs(state),
    authorities: readonly(state.authorities),
    hasAuthority,
    isCurrentUserAdmin,
    isMobileVerified,
    isSameUser,
    logout,
    setUserInfo,
    setUserState,
  };
}
